import React, { useEffect, useState } from "react";
import LegendBadge from "./LegendBadge";
import { ModalHistoryRow } from "./HistoryRows";
import { getPercentage, formatDate, getModeLabel } from "./analyticsUtils";

const HistoryModal = ({ historyRows, getReviewerTitle, onClose, onDeleteResult, onBatchDelete, onReviewModule }) => {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Close on backdrop click
  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const validRowIds = historyRows.map((r) => r._id).filter(Boolean);
  const isAllSelected = validRowIds.length > 0 && validRowIds.every((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(validRowIds));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    if (onBatchDelete) {
      onBatchDelete(Array.from(selectedIds), () => setSelectedIds(new Set()));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}
      onClick={handleBackdrop}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col"
        style={{ maxHeight: "88vh", animation: "modalSlideIn 0.22s cubic-bezier(.4,0,.2,1)" }}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600">
              <i className="fa-solid fa-clock-rotate-left text-sm" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-lg leading-tight">All Quiz History</h2>
              <p className="text-xs text-gray-400">
                {historyRows.length} session{historyRows.length !== 1 ? "s" : ""} total
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-800 transition"
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark text-sm" />
          </button>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-2 px-6 py-2.5 bg-gray-50 border-b border-gray-100 flex-shrink-0">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mr-1">Legend:</span>
          <LegendBadge color="green" label="Mastered" range="≥ 80%" />
          <LegendBadge color="orange" label="Progressing" range="60–79%" />
          <LegendBadge color="red" label="Needs Review" range="< 60%" />
        </div>

        {/* Bulk Action Banner */}
        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between px-6 py-2.5 bg-rose-50 border-b border-rose-100 flex-shrink-0 animate-fadeIn">
            <span className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
              <i className="fa-solid fa-check-double text-rose-600" />
              <span>{selectedIds.size} attempt{selectedIds.size > 1 ? "s" : ""} selected</span>
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                className="px-3 py-1 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-lg shadow-sm flex items-center gap-1.5 transition active:scale-95"
              >
                <i className="fa-solid fa-trash-can text-xs" />
                Delete Selected ({selectedIds.size})
              </button>
            </div>
          </div>
        )}

        {/* Scrollable Table */}
        <div className="overflow-y-auto flex-1 px-6 pb-6">
          {historyRows.length === 0 ? (
            <div className="py-16 text-center text-gray-400">No quizzes taken yet.</div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-xs text-gray-400 border-b border-gray-100 uppercase tracking-wide">
                  <th className="py-3 pl-3 w-10 text-center">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300 cursor-pointer transition"
                      aria-label="Select all history rows"
                    />
                  </th>
                  <th className="py-3 pl-2 font-semibold">#</th>
                  <th className="py-3 font-semibold">Subject</th>
                  <th className="py-3 font-semibold">Date</th>
                  <th className="py-3 font-semibold">Type</th>
                  <th className="py-3 font-semibold">Score</th>
                  <th className="py-3 font-semibold">Proficiency Status</th>
                  <th className="py-3 pr-2 text-center font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {historyRows.map((item, idx) => {
                  const pct = getPercentage(item) || 0;
                  const statusColor = pct >= 80 ? "green" : pct >= 60 ? "orange" : "red";
                  const status = pct >= 80 ? "Mastered" : pct >= 60 ? "Progressing" : "Needs Review";
                  return (
                    <ModalHistoryRow
                      key={item._id || idx}
                      rank={idx + 1}
                      subject={getReviewerTitle(item)}
                      date={formatDate(item.createdAt)}
                      type={getModeLabel(item.mode)}
                      score={`${pct}%`}
                      status={status}
                      statusColor={statusColor}
                      isSelected={selectedIds.has(item._id)}
                      onToggleSelect={() => toggleSelectOne(item._id)}
                      onReview={onReviewModule ? () => onReviewModule(item) : undefined}
                      onStatusClick={onReviewModule ? () => onReviewModule(item) : undefined}
                      onTitleClick={onReviewModule ? () => onReviewModule(item) : undefined}
                      onDelete={onDeleteResult ? () => onDeleteResult(item._id) : undefined}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex-shrink-0 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            Showing all <span className="font-bold text-gray-700">{historyRows.length}</span> results
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-700 transition"
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalSlideIn {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
      `}</style>
    </div>
  );
};

export default HistoryModal;
